/**
 * Craft facts the pane needs but the wire does not carry yet (YAG-591).
 *
 * One of the registry's per-craft facts is presentation, not storage: which
 * Load dials this craft actually reads. The engagement response does not carry
 * it today, so it lives here as a small map keyed by slug, and the reader
 * prefers the wire the moment the field appears, so this map retires by
 * deletion rather than by a second migration.
 *
 * A slug this map has never heard of degrades honestly: no dials.
 */
import type {
  EngagementLoadDial,
  Schedule,
  WorkerSummary,
} from "@yagni/shared";
import { WEEKDAYS } from "./scheduleSentence.js";

/**
 * The two review crafts, by slug, named once.
 *
 * Both were spelled as bare `"reeve"` / `"proctor"` literals in the case file,
 * the amendment label and the worker's own output page — three places that
 * have to agree and no way to notice when they stop. The registry owns the
 * slug; these are the frontend's single reference to it.
 */
export const PLAN_REVIEW_SLUG = "reeve";
export const CODE_REVIEW_SLUG = "proctor";

/** Which Load dials each craft's pipeline actually reads. */
const CRAFT_DIALS: Record<string, readonly EngagementLoadDial[]> = {
  wright: ["inFlight", "revisionRounds"],
  bailey: ["perRun"],
  fletcher: ["perRun"],
};

/**
 * Which crafts' pipelines actually read the engagement's Scope. Harper's does
 * not: jobs gather from the Team's attached repositories and never narrow by
 * engagement Scope, so the fold would be a control writing config nothing
 * reads. Repos are set once on the Team (Workers & attachments) and every
 * job reads those. (Reeve's worker-request path asserts repo scope,
 * `workers/requests/commands.ts#requireWorkerScope`.)
 */
const CRAFT_SCOPE_CONSUMED = new Set<string>(["proctor", "bailey", "wright", "reeve"]);

/**
 * Which crafts have a supervised review-queue enqueue path (their rows land
 * in engagement_review_queue). A band with no possible rows is a
 * permanently-empty lie: Wright (pr handoff), Reeve (plan/proposal reads).
 */
const CRAFT_REVIEW_QUEUED = new Set<string>(["wright", "reeve"]);

/** True where the engagement's Scope actually narrows this craft's reads. */
export function craftScopeConsumed(worker: WorkerSummary): boolean {
  return CRAFT_SCOPE_CONSUMED.has(worker.slug);
}

/** True where a supervised review-queue row can ever exist for this craft. */
export function craftReviewQueued(slug: string): boolean {
  return CRAFT_REVIEW_QUEUED.has(slug);
}

/** The Load dials this craft reads; empty renders no Load controls at all. */
export function craftDials(worker: WorkerSummary): readonly EngagementLoadDial[] {
  return worker.legDefaults?.dials ?? CRAFT_DIALS[worker.slug] ?? [];
}

/**
 * The schedule a craft starts on (YAG-647 §2.4), mirroring the registry's own
 * seeds. The editor opens on this only when the row carries no schedule the
 * sentence editor can round-trip; an existing row always wins, because a
 * default that quietly overwrites a person's schedule is not a default.
 */
const CRAFT_DEFAULT_SCHEDULE: Record<string, Schedule> = {
  bailey: {
    every: { minutes: 120, between: ["08:00", "18:00"] },
    days: [...WEEKDAYS],
  },
  harper: { times: ["09:00"], days: [...WEEKDAYS] },
  wright: { times: ["08:00", "12:00", "17:00"], days: [...WEEKDAYS] },
};

/** Where the editor starts for a craft whose row has no readable schedule. */
export function craftDefaultSchedule(slug: string): Schedule {
  return CRAFT_DEFAULT_SCHEDULE[slug] ?? { times: ["09:00"], days: [...WEEKDAYS] };
}
